/**
 * @fileoverview Context used to keep track of the active session state.
 * This context should render only once for the entirety of the active session otherwise the stored count will reset.
 */

import { useContext, createContext, useReducer, ReactNode } from 'react';

import { MovementType } from '@/types/MovementTypes';

export type AnnotationCounts = Map<MovementType, number>;

type Action = { type: 'INCREASE'; payload: MovementType } | { type: 'RESET' };

// Reducer function to update the state based on the action type.
// We always return a new Map to trigger re-renders.
function annotationCountsReducer(state: AnnotationCounts, action: Action): AnnotationCounts {
  switch (action.type) {
    case 'INCREASE': {
      const newState = new Map(state);
      const currentCount = newState.get(action.payload) ?? 0;
      newState.set(action.payload, currentCount + 1);

      return newState;
    }

    case 'RESET': {
      return new Map<MovementType, number>();
    }

    default:
      return state;
  }
}

// Define the context interface that includes the state
// and the methods to update it.
interface AnnotationContextType {
  state: AnnotationCounts;
  increase: (annotation: MovementType) => void;
  resetAnnotations: VoidFunction;
}

const AnnotationCountsContext = createContext<AnnotationContextType | null>(null);

export const AnnotationCountsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(annotationCountsReducer, new Map<MovementType, number>());

  /**
   * Method to increase the count for a given annotation.
   * @param annotation The annotation to increase the count for.
   */
  const increase = (annotation: MovementType) => {
    dispatch({ type: 'INCREASE', payload: annotation });
  };

  /**
   * Method to call after a session is finished to reset the annotation counts.
   */
  const resetAnnotations = () => {
    dispatch({ type: 'RESET' });
  };

  const value: AnnotationContextType = {
    state,
    increase,
    resetAnnotations,
  };

  return <AnnotationCountsContext.Provider value={value}>{children}</AnnotationCountsContext.Provider>;
};

export const useAnnotationCounts = (): AnnotationContextType => {
  const context = useContext(AnnotationCountsContext);

  if (!context) {
    throw new Error('useAnnotationCounts must be used within an AnnotationCountsProvider');
  }

  return context;
};
